
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { useEmailVerification } from '@/hooks/useEmailVerification';

interface EmailOtpVerificationProps {
  email: string;
  onVerified: () => void;
}

export const EmailOtpVerification = ({ 
  email, 
  onVerified
}: EmailOtpVerificationProps) => {
  const [emailOtp, setEmailOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  const {
    loading,
    verifyOtp,
    setEmail: setVerificationEmail
  } = useEmailVerification({
    onVerificationComplete: onVerified
  });
  
  // Make sure the email is set in the hook
  useEffect(() => {
    if (email) {
      setVerificationEmail(email);
    }
  }, [email, setVerificationEmail]);
  
  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailOtp) return;
    
    setError(null);
    
    try {
      await verifyOtp(emailOtp);
    } catch (error: any) {
      setError(error.message || 'Failed to verify email OTP');
    }
  };
  
  return (
    <form onSubmit={handleVerifyEmail} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="emailOtp">Email Verification Code</Label>
        <Input
          id="emailOtp"
          type="text"
          placeholder="Enter email verification code"
          value={emailOtp}
          onChange={(e) => setEmailOtp(e.target.value)}
          maxLength={6}
          disabled={loading}
        />
        <p className="text-xs text-muted-foreground">
          Check your email for a 6-digit verification code
        </p>
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <Button 
        type="submit" 
        disabled={!emailOtp || loading}
        className="w-full"
      >
        {loading ? 'Verifying...' : 'Verify Email'}
      </Button>
    </form>
  );
};
